import { DIAGRAM_API_CONFIG, assertDiagramApiConfiguration } from '../config/diagramApiConfig';
import type { DiagramSpec } from '../types/diagramSpec';
import type { ApiResult, AuthTokenProvider, RequestOptions, UploadFileInput } from '../types/api';
import {
  applyDiagramRenderEvent,
  createDiagramRenderState,
  createDiagramWriteHeaders,
  parseDiagramSseEvents,
  type DiagramRenderEvent,
  type DiagramRenderState,
} from '../utils/diagramApiSupport';
import { ApiError, appendUploadFileToFormData, createApiClient, getStoredAccessToken } from './client';

export type DiagramSourceDto = {
  type: 'photo' | 'scan' | 'generated';
  image_ref: string;
  text_ref?: string;
};

export type DiagramSpecRecordDto = {
  diagram_id: string;
  current_revision_id: string;
  current_revision_no: number;
  status: DiagramSpec['status'];
  source: DiagramSourceDto;
  created_at: string;
  updated_at: string;
  current_revision?: DiagramRevisionDto;
  rendering?: DiagramRenderJobDto | null;
};

export type DiagramRevisionDto = {
  revision_id: string;
  diagram_id: string;
  revision_no: number;
  status: DiagramSpec['status'];
  change_reason: 'created' | 'user_correction' | 'algorithm_update' | 'source_update' | 'constraint_update';
  spec: DiagramSpec;
  created_at: string;
};

export type DiagramRenderResultDto = {
  result_id: string;
  revision_id: string;
  artifact_ref: string;
  fallback: { mode: 'original_image'; image_ref: string };
  accessibility_summary: string;
};

export type DiagramRenderJobDto = {
  render_job_id: string;
  revision_id: string;
  status: 'queued' | 'running' | 'succeeded' | 'failed' | 'cancelled';
  trace_id: string;
  run_id: string;
  node_id: string;
  attempt: number;
  result?: DiagramRenderResultDto;
};

export type DiagramRenderAcceptedDto = {
  render_job_id: string;
  status: 'queued';
  stream_url: string;
  trace_id: string;
  run_id: string;
};

export type DiagramUploadDto = {
  image_ref: string;
  url: string;
  content_type: string;
  size_bytes: number;
};

type DiagramRequestClient = {
  request: <TResponse, TBody = unknown>(
    path: string,
    options?: RequestOptions<TBody>
  ) => Promise<ApiResult<TResponse>>;
};

type DiagramSpecApiOptions = {
  baseUrl?: string | null;
  getToken?: AuthTokenProvider;
  client?: DiagramRequestClient;
  fetchImpl?: typeof fetch;
};

const joinUrl = (baseUrl: string, path: string): string => {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  return `${baseUrl.replace(/\/$/, '')}${path.startsWith('/') ? path : `/${path}`}`;
};

const parseStreamError = async (response: Response): Promise<ApiError> => {
  let message = 'Diagram render stream request failed';
  let code = 'unknown';
  try {
    const payload = await response.json() as { error?: { code?: string; message?: string }; detail?: { code?: string; message?: string } | string };
    const detail = typeof payload.detail === 'object' && payload.detail ? payload.detail : payload.error;
    if (detail && typeof detail === 'object') {
      message = detail.message || message;
      code = detail.code || code;
    } else if (typeof payload.detail === 'string') {
      message = payload.detail;
    }
  } catch {
    // Preserve the protocol-level failure when the gateway returns non-JSON.
  }
  return new ApiError(message, code as never, response.status);
};

export const createDiagramSpecApi = ({
  baseUrl = DIAGRAM_API_CONFIG.baseUrl,
  getToken = getStoredAccessToken,
  client,
  fetchImpl = fetch,
}: DiagramSpecApiOptions = {}) => {
  const configurationError = assertDiagramApiConfiguration();
  if (configurationError || !baseUrl) {
    throw new Error(configurationError || 'DiagramSpec staging API is not configured');
  }

  const apiBaseUrl = baseUrl;
  const requestClient = client ?? createApiClient({ baseUrl: apiBaseUrl, getToken });
  const request = <TResponse, TBody = unknown>(
    path: string,
    options: RequestOptions<TBody>
  ) => requestClient.request<TResponse, TBody>(path, options);

  const getRenderJob = async (renderJobId: string): Promise<DiagramRenderJobDto> =>
    (await request<DiagramRenderJobDto>(`/api/v1/diagram-render-jobs/${encodeURIComponent(renderJobId)}`, {})).data;

  return {
    uploadImage: async (file: UploadFileInput): Promise<DiagramUploadDto> => {
      const formData = new FormData();
      await appendUploadFileToFormData(formData, 'file', file);
      formData.append('purpose', 'agent');
      return (await request<DiagramUploadDto>('/api/v1/uploads/image', { method: 'POST', formData })).data;
    },
    createDiagram: async (source: DiagramSourceDto, spec: DiagramSpec, idempotencyKey: string): Promise<DiagramSpecRecordDto> =>
      (await request<DiagramSpecRecordDto>('/api/v1/diagram-specs', {
        method: 'POST',
        body: { source, spec },
        headers: createDiagramWriteHeaders(idempotencyKey),
      })).data,
    getDiagram: async (diagramId: string): Promise<DiagramSpecRecordDto> =>
      (await request<DiagramSpecRecordDto>(`/api/v1/diagram-specs/${encodeURIComponent(diagramId)}`, {})).data,
    createRevision: async (
      diagramId: string,
      baseRevisionNo: number,
      changeReason: Exclude<DiagramRevisionDto['change_reason'], 'created'>,
      spec: DiagramSpec,
      idempotencyKey: string
    ): Promise<DiagramRevisionDto> =>
      (await request<DiagramRevisionDto>(`/api/v1/diagram-specs/${encodeURIComponent(diagramId)}/revisions`, {
        method: 'POST',
        body: { base_revision_no: baseRevisionNo, change_reason: changeReason, spec },
        headers: createDiagramWriteHeaders(idempotencyKey),
      })).data,
    submitRenderJob: async (
      payload: Omit<DiagramRenderAcceptedDto, 'render_job_id' | 'status' | 'stream_url'> & { revision_id: string; render_profile: 'student_canvas_v1'; node_id: string; attempt: number },
      idempotencyKey: string
    ): Promise<DiagramRenderAcceptedDto> =>
      (await request<DiagramRenderAcceptedDto>('/api/v1/diagram-render-jobs', {
        method: 'POST',
        body: payload,
        headers: createDiagramWriteHeaders(idempotencyKey),
      })).data,
    getRenderJob,
    cancelRenderJob: async (renderJobId: string, idempotencyKey: string): Promise<DiagramRenderJobDto> =>
      (await request<DiagramRenderJobDto>(`/api/v1/diagram-render-jobs/${encodeURIComponent(renderJobId)}/cancel`, {
        method: 'POST',
        headers: createDiagramWriteHeaders(idempotencyKey),
      })).data,
    consumeRenderEvents: async (
      renderJobId: string,
      streamUrl: string,
      onEvent?: (event: DiagramRenderEvent, state: DiagramRenderState) => void,
      signal?: AbortSignal
    ): Promise<DiagramRenderState> => {
      const token = await getToken();
      let response: Response;
      try {
        response = await fetchImpl(joinUrl(apiBaseUrl, streamUrl), {
          headers: { Accept: 'text/event-stream', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          signal,
        });
      } catch {
        await getRenderJob(renderJobId);
        throw new ApiError('Diagram render stream disconnected', 'network');
      }
      if (!response.ok) {
        throw await parseStreamError(response);
      }

      let state = createDiagramRenderState();
      try {
        const events = parseDiagramSseEvents(await response.text());
        for (const event of events) {
          state = applyDiagramRenderEvent(state, event);
          onEvent?.(event, state);
        }
        return state;
      } catch (error) {
        await getRenderJob(renderJobId);
        throw error;
      }
    },
  };
};
